/**
 * Shared confirmation modal for the Rules page.
 *
 * Used by:
 *   - Apply to past transactions (info variant)
 *   - Delete rule (danger variant)
 *
 * React Portal into document.body. Centered on viewport, backdrop
 * dims the page, Escape closes, backdrop click closes.
 *
 * Variant decides the icon color and the primary button color:
 *   - info:   purple Vela icon, dark ink primary button
 *   - danger: red icon, red destructive primary button
 *
 * Optional stat row shows a number + label (e.g. "142 past
 * transactions will be re-categorized"). Optional footerNote
 * shows a small helper line below the body.
 */

import { useEffect, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { RotateCcw, Trash2 } from "lucide-react";

const PALETTE = {
  ink: "#0E1A1A",
  ink2: "#263238",
  ink3: "#3A4A52",
  ink4: "#6B7684",
  line: "#D8DEE6",
  bgFade: "#F3F5F5",
  vela: "#6B46C1",
  velaWash: "#F5F3FF",
  red: "#B42318",
  redWash: "#FEF3F2",
};

export type ConfirmModalVariant = "info" | "danger";

interface ConfirmModalProps {
  isOpen: boolean;
  variant: ConfirmModalVariant;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  statCount?: number;
  statLabel?: string;
  footerNote?: ReactNode;
  onConfirm: () => void;
  onClose: () => void;
}

export function ConfirmModal({
  isOpen,
  variant,
  title,
  description,
  confirmLabel,
  cancelLabel = "Cancel",
  statCount,
  statLabel,
  footerNote,
  onConfirm,
  onClose,
}: ConfirmModalProps) {
  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isInfo = variant === "info";
  const Icon = isInfo ? RotateCcw : Trash2;
  const iconColor = isInfo ? PALETTE.vela : PALETTE.red;
  const iconBg = isInfo ? PALETTE.velaWash : PALETTE.redWash;

  const confirmBtnStyle: CSSProperties = isInfo ? primaryBtnStyle : dangerBtnStyle;

  return createPortal(
    <div
      style={overlayStyle}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        style={modalStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ ...iconWrapStyle, background: iconBg, color: iconColor }}>
          <Icon size={22} color={iconColor} strokeWidth={2} />
        </div>

        <h3 style={titleStyle}>{title}</h3>
        <div style={bodyStyle}>{description}</div>

        {statCount !== undefined && statLabel && (
          <div style={statRowStyle}>
            <div>
              <div style={statNumStyle}>{statCount.toLocaleString("en-CA")}</div>
              <div style={statLabelStyle}>{statLabel}</div>
            </div>
          </div>
        )}

        {footerNote && <div style={footerNoteStyle}>{footerNote}</div>}

        <div style={actionsStyle}>
          <button type="button" style={ghostBtnStyle} onClick={onClose}>
            {cancelLabel}
          </button>
          <button type="button" style={confirmBtnStyle} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const overlayStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(14, 26, 26, 0.42)",
  zIndex: 2500,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
};

const modalStyle: CSSProperties = {
  background: "#ffffff",
  borderRadius: 14,
  maxWidth: 440,
  width: "100%",
  padding: 26,
  boxShadow: "0 24px 48px -6px rgba(14, 26, 26, 0.3)",
};

const iconWrapStyle: CSSProperties = {
  width: 44,
  height: 44,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 16,
};

const titleStyle: CSSProperties = {
  fontSize: 17,
  fontWeight: 700,
  color: PALETTE.ink,
  margin: "0 0 8px",
  letterSpacing: "-0.01em",
};

const bodyStyle: CSSProperties = {
  fontSize: 13.5,
  color: PALETTE.ink3,
  lineHeight: 1.55,
  marginBottom: 22,
};

const statRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "12px 14px",
  background: PALETTE.bgFade,
  borderRadius: 8,
  margin: "14px 0 18px",
  fontSize: 13,
};

const statNumStyle: CSSProperties = {
  fontSize: 20,
  fontWeight: 800,
  color: PALETTE.ink,
};

const statLabelStyle: CSSProperties = {
  fontSize: 12,
  color: PALETTE.ink3,
  fontWeight: 500,
};

const footerNoteStyle: CSSProperties = {
  fontSize: 12,
  color: PALETTE.ink4,
  marginBottom: 18,
  lineHeight: 1.55,
};

const actionsStyle: CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 8,
};

const ghostBtnStyle: CSSProperties = {
  background: "transparent",
  border: `1px solid ${PALETTE.line}`,
  color: PALETTE.ink2,
  fontSize: 13,
  fontWeight: 600,
  padding: "9px 16px",
  borderRadius: 8,
  cursor: "pointer",
  fontFamily: "inherit",
};

const primaryBtnStyle: CSSProperties = {
  background: PALETTE.ink,
  color: "#ffffff",
  border: 0,
  fontSize: 13,
  fontWeight: 600,
  padding: "10px 18px",
  borderRadius: 8,
  cursor: "pointer",
  fontFamily: "inherit",
};

const dangerBtnStyle: CSSProperties = {
  background: PALETTE.red,
  color: "#ffffff",
  border: 0,
  fontSize: 13,
  fontWeight: 600,
  padding: "10px 18px",
  borderRadius: 8,
  cursor: "pointer",
  fontFamily: "inherit",
};